import { HttpErrorResponse } from '@angular/common/http';
import { ErrorCategory } from '../models/error-log.model';

/**
 * Defensive-programming helpers shared by services, interceptors, guards and
 * the global error handler. Pure functions only — no DI, no side effects —
 * so they can be reused anywhere (including sub-projects) without pulling in
 * Angular's injector.
 *
 * See GLOBAL_TRY_CATCH_AND_ERROR_HANDLING_GUIDELINES.md.
 */

/** Type guard so callers can narrow `unknown` without an `any` cast. */
export function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}

/**
 * Generates a client-side correlation id when the backend response does not
 * supply one, so a single failed operation can still be traced in logs.
 */
export function generateCorrelationId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older browsers/tests).
  // Deliberately uses crypto.getRandomValues, NOT Math.random() (Sonar S2245:
  // Math.random() is not a cryptographically secure PRNG).
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(8));
    return `${Date.now()}-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `${Date.now()}`;
}

/** Extracts a short, non-technical-safe message from any thrown/rejected value. */
export function extractErrorMessage(error: unknown): string {
  if (isHttpErrorResponse(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

/** Categorizes an arbitrary caught value into one of the standard ErrorCategory buckets. */
export function categorizeError(error: unknown): ErrorCategory {
  if (isHttpErrorResponse(error)) {
    return 'HttpError';
  }
  return 'RuntimeError';
}
